(function () {
  /* ==========================================================================
     CONFIGURATION
  ========================================================================== */
  const TARGET_SELECTOR  = 'cart-drawer .drawer__footer .cart__ctas';
  const WRAPPER_SELECTOR = '[data-cart-discount-wrapper]';
  const STORAGE_KEY      = 'ck_active_discount_code';
  const CHECKOUT_FLAG_KEY = 'ck_checkout_with_code';

  function getFreeGiftCore() {
    return typeof window !== 'undefined' ? window.FreeGiftCore : null;
  }

  function isTier2ThemeGiftCode(code) {
    const core = getFreeGiftCore();
    if (core && typeof core.isTier2ThemeCode === 'function') {
      return core.isTier2ThemeCode(code);
    }
    const s = window.freeGiftSettings;
    if (!s || !s.promoGift || !s.promoGift.enabled) return false;
    const upper = String(code || '').trim().toUpperCase();
    if (!upper) return false;
    const codes = (s.promoGift.code || '').split(/[\n,]+/);
    return codes.some((c) => c.trim().toUpperCase() === upper);
  }

  function getAppliedTier2ThemeCode() {
    const core = getFreeGiftCore();
    if (core && typeof core.getAppliedTier2Code === 'function') {
      return core.getAppliedTier2Code();
    }
    try {
      return sessionStorage.getItem('ck_tier2_gift_code') || localStorage.getItem('ck_tier2_gift_code');
    } catch (e) {
      return null;
    }
  }

  function getPendingTier2ThemeCode() {
    const core = getFreeGiftCore();
    if (core && typeof core.getPendingTier2Code === 'function') {
      return core.getPendingTier2Code();
    }
    try {
      return (
        sessionStorage.getItem('ck_tier2_pending_gift_code') ||
        localStorage.getItem('ck_tier2_pending_gift_code')
      );
    } catch (e) {
      return null;
    }
  }

  function setAppliedTier2ThemeCode(code) {
    const core = getFreeGiftCore();
    if (core && typeof core.setAppliedTier2Code === 'function') {
      core.setAppliedTier2Code(code);
      return;
    }
    try {
      const upper = String(code).trim().toUpperCase();
      sessionStorage.setItem('ck_tier2_gift_code', upper);
      localStorage.setItem('ck_tier2_gift_code', upper);
    } catch (e) {}
  }

  function clearAppliedTier2ThemeCode() {
    const core = getFreeGiftCore();
    if (core && typeof core.clearAllTier2CodeState === 'function') {
      core.clearAllTier2CodeState();
      return;
    }
    if (core && typeof core.clearAppliedTier2Code === 'function') {
      core.clearAppliedTier2Code();
    }
    try {
      sessionStorage.removeItem('ck_tier2_gift_code');
      localStorage.removeItem('ck_tier2_gift_code');
      sessionStorage.removeItem('ck_tier2_pending_gift_code');
      localStorage.removeItem('ck_tier2_pending_gift_code');
    } catch (e) {}
  }

  function captureTier2CodeFromDiscountLink() {
    const core = getFreeGiftCore();
    if (core && typeof core.captureTier2CodeFromLanding === 'function') {
      return core.captureTier2CodeFromLanding();
    }
    return false;
  }

  /** Shopify sets this cookie for every /discount/CODE link visit. */
  function readShopifyDiscountCodeCookie() {
    try {
      const parts = document.cookie.split(';');
      for (const part of parts) {
        const trimmed = part.trim();
        const eq = trimmed.indexOf('=');
        if (eq < 0) continue;
        if (trimmed.slice(0, eq).trim().toLowerCase() !== 'discount_code') continue;
        return decodeURIComponent(trimmed.slice(eq + 1).trim());
      }
    } catch (e) {}
    return null;
  }

  /**
   * After a native /discount/CODE link (e.g. HELLO-22378), Shopify stores the code in a
   * cookie. Apply it to the cart once items exist. Skips codes that match promo_gift_code
   * (Tier 2 theme codes) — those are handled by free-gift.js instead.
   */
  async function applyNativeDiscountFromLinkCookieIfNeeded(cart) {
    if (!cart || !cart.item_count) return cart;
    if (getApplicableDiscountCode(cart)) return cart;

    const cookieCode = readShopifyDiscountCodeCookie();
    if (!cookieCode || isTier2ThemeGiftCode(cookieCode)) return cart;

    const upper = String(cookieCode).trim().toUpperCase();
    const { success, cart: updated } = await applyCodeToSession(upper, 2);
    if (!success) return cart;

    _liveNativeCode = upper;
    persistCode(upper);
    /* One promo at a time: a native code replaces any Tier 2 theme gift code. */
    if (getAppliedTier2ThemeCode()) {
      clearAppliedTier2ThemeCode();
      hideAppliedKind('tier2');
      await notifyTier2GiftSync();
    }
    let settledCart = updated || cart;
    if (await syncFreeGiftAfterDiscountChange()) {
      try { settledCart = await fetchCart(); } catch (_) {}
    }
    return settledCart;
  }

  /**
   * Free-gift tiers are measured against the discounted merchandise total, but free-gift.js
   * only reacts to cart:updated and Tier 2 code events. A native code that dropped the cart
   * under the Tier 1 threshold therefore left the gift sitting in the cart. Returns true when
   * a sync actually ran, so callers know to re-read the cart.
   */
  async function syncFreeGiftAfterDiscountChange() {
    const mgr = window.freeGiftManager;
    if (!mgr || typeof mgr.syncGiftForced !== 'function') return false;
    try {
      await mgr.syncGiftForced();
      /* free-gift.js refreshes the drawer when it mutates — restore active overlay. */
      ensureCartDrawerDiscountLoader();
      ensureCartDrawerGiftSyncLoader();
      return true;
    } catch (e) {
      console.warn('[Discount] Free gift sync after discount change failed', e);
      ensureCartDrawerDiscountLoader();
      ensureCartDrawerGiftSyncLoader();
      return false;
    }
  }

  async function notifyTier2GiftSync() {
    document.dispatchEvent(new CustomEvent('free-gift:tier2-code-changed'));
    if (window.freeGiftManager && typeof window.freeGiftManager.syncGiftForced === 'function') {
      try {
        await window.freeGiftManager.syncGiftForced();
      } catch (e) {
        console.warn('[Discount] Tier 2 gift sync failed', e);
      }
    }
    ensureCartDrawerDiscountLoader();
  }

  /** After native Shopify discount apply/remove, re-run free-gift threshold sync. */
  async function notifyFreeGiftSync() {
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'discount-minicart' } }));
    if (window.freeGiftManager && typeof window.freeGiftManager.syncGiftForced === 'function') {
      try {
        await window.freeGiftManager.syncGiftForced();
      } catch (e) {
        console.warn('[Discount] Free gift sync failed', e);
      }
    }
  }

  function showTier2ThemeApplied(code) {
    /* Scenario 1: Tier 2 replaces Shopify — never leave WELCOME/ENJOY pill visible. */
    hideAppliedKind('shopify');
    showAppliedKind('tier2', code);
  }

  function tier2ThresholdErrorMessage() {
    const pg = window.freeGiftSettings && window.freeGiftSettings.promoGift;
    let threshold = pg && pg.threshold != null ? Number(pg.threshold) : NaN;
    if (!(threshold > 0)) {
      const el = document.querySelector('[data-error-tier2-threshold]');
      const raw = el && el.getAttribute('data-error-tier2-threshold');
      threshold = raw != null && raw !== '' ? Number(raw) : 150;
    }
    if (!(threshold > 0)) threshold = 150;
    return 'Minimum $' + Math.round(threshold) + ' product purchase required';
  }

  function getDiscountErrorCopy(attrSuffix, fallback) {
    const el =
      document.querySelector('[data-discount-error]') ||
      document.querySelector('[data-error-' + attrSuffix + ']');
    if (!el) return fallback;
    const fromAttr = el.getAttribute('data-error-' + attrSuffix);
    return fromAttr && String(fromAttr).trim() ? String(fromAttr).trim() : fallback;
  }

  /**
   * Pick a real error for a failed Shopify discount apply.
   * Never default to the old hardcoded "$75" message — that was shown for every failure.
   */
  function shopifyDiscountApplyErrorMessage(code, cart) {
    const upper = String(code || '').trim().toUpperCase();
    const codes = (cart && Array.isArray(cart.discount_codes) ? cart.discount_codes : []) || [];
    const entry = codes.find((dc) => String(dc.code || '').trim().toUpperCase() === upper);

    if (entry && entry.applicable === false) {
      return getDiscountErrorCopy(
        'not-applicable',
        'Code invalid, expired, or cart minimum not met'
      );
    }

    // Code never became applicable (invalid / expired / not found).
    return getDiscountErrorCopy('invalid', 'Code invalid, expired, or cart minimum not met');
  }

  async function cartMeetsTier2Threshold() {
    const core = getFreeGiftCore();
    if (!core) return true;
    try {
      const cart = await fetchCart();
      if (typeof core.meetsTier2PromotionThreshold === 'function') {
        return core.meetsTier2PromotionThreshold(cart);
      }
      if (typeof core.meetsTier2Threshold === 'function') {
        return core.meetsTier2Threshold(cart);
      }
      return true;
    } catch (_) {
      return false;
    }
  }

  /**
   * One customer promo at a time in the UI:
   * - Native Shopify code (WELCOME/ENJOY) → show Shopify pill only; clear Tier 2 + gift
   * - Tier 2 theme code (APPLE) alone → show Tier 2 pill only
   *
   * Do NOT clear ck_tier2_gift_code here on a threshold miss — free-gift.js owns
   * code clear (with settle/recheck). Clearing from the discount UI after
   * FreeGift→Bundle assign caused a false clear when discounted component prices
   * briefly undercounted vs the $135 Tier 2 threshold (single-bundle case).
   */
  function restoreAppliedDiscountPill(cart) {
    ensureDiscountUiReady();

    const nativeCode = cart ? getApplicableDiscountCode(cart) : _liveNativeCode;
    if (nativeCode) {
      _liveNativeCode = nativeCode;
      showAppliedKind('shopify', nativeCode);
      persistCode(nativeCode);
      /* Scenario 2: Shopify promo wins — never paint APPLE beside WELCOME/ENJOY. */
      hideAppliedKind('tier2');
      if (getAppliedTier2ThemeCode() || getPendingTier2ThemeCode()) {
        clearAppliedTier2ThemeCode();
        notifyTier2GiftSync();
      }
      return;
    }

    if (!cart) {
      /* Never paint Shopify pill from storage alone — that ghosts removed codes
         (e.g. ENJOY) after refresh when Buy 4 / other autos still discount the cart. */
      const saved = getPersistedCode();
      if (!saved || isTier2ThemeGiftCode(saved)) {
        hideAppliedKind('shopify');
      } else {
        fetchCart()
          .then((live) => {
            const applicable = getApplicableDiscountCode(live);
            if (applicable && applicable.toUpperCase() === String(saved).toUpperCase()) {
              _liveNativeCode = applicable;
              showAppliedKind('shopify', applicable);
              persistCode(applicable);
              hideAppliedKind('tier2');
              if (getAppliedTier2ThemeCode() || getPendingTier2ThemeCode()) {
                clearAppliedTier2ThemeCode();
                notifyTier2GiftSync();
              }
            } else {
              clearPersistedCode();
              hideAppliedKind('shopify');
              const tier2Theme = resolveTier2CodeForPill(live);
              if (tier2Theme) showAppliedKind('tier2', tier2Theme);
              else hideAppliedKind('tier2');
            }
          })
          .catch(() => {
            hideAppliedKind('shopify');
          });
        return;
      }
    } else {
      /* Section refresh responses often omit discount_codes — keep the pill from memory. */
      const saved = _liveNativeCode || getPersistedCode();
      if (saved && !isTier2ThemeGiftCode(saved)) {
        showAppliedKind('shopify', saved);
        hideAppliedKind('tier2');
        if (getAppliedTier2ThemeCode() || getPendingTier2ThemeCode()) {
          clearAppliedTier2ThemeCode();
          notifyTier2GiftSync();
        }
        return;
      }
      hideAppliedKind('shopify');
    }

    const tier2Theme = resolveTier2CodeForPill(cart);
    if (tier2Theme) {
      showAppliedKind('tier2', tier2Theme);
    } else {
      hideAppliedKind('tier2');
    }
  }

  function cartMeetsTier2ForPillDisplay(cart) {
    const core = getFreeGiftCore();
    if (!core) return false;
    if (typeof core.meetsTier2PromotionThreshold === 'function') {
      return core.meetsTier2PromotionThreshold(cart);
    }
    if (!cart) return false;
    if (typeof core.meetsGiftThresholdForTier === 'function') {
      const pg = window.freeGiftSettings && window.freeGiftSettings.promoGift;
      const min = pg && pg.threshold != null ? Math.round(Number(pg.threshold) * 100) : 12500;
      return core.meetsGiftThresholdForTier(cart, min);
    }
    return false;
  }

  /** Shipping page — repaint pills from storage immediately after section HTML swap. */
  function restoreShippingDiscountPillsImmediate(cartData) {
    if (!isShippingPage()) return;
    ensureDiscountUiReady();
    restoreAppliedDiscountPill(cartData);
  }

  /** Applied code only — pending link codes must not paint a pill beside WELCOME/ENJOY. */
  function resolveTier2CodeForPill(cart) {
    const applied = getAppliedTier2ThemeCode();
    if (applied) return applied;
    /* Do not paint pending while a native Shopify code is (or was) live. */
    if (_liveNativeCode || (cart && getApplicableDiscountCode(cart))) return null;
    const pending = getPendingTier2ThemeCode();
    if (!pending) return null;
    if (cart && cartMeetsTier2ForPillDisplay(cart)) return pending;
    return null;
  }

  let _pillRestoreTimer = null;
  let _pillRestoreInFlight = null;
  let _pillRestoreQueued = false;
  let _discountPillSyncDepth = 0;

  function ensureDiscountSyncStyles() {
    if (document.getElementById('ck-discount-sync-style')) return;
    const style = document.createElement('style');
    style.id = 'ck-discount-sync-style';
    style.textContent =
      '.cart__discount--syncing{opacity:.6;pointer-events:none;position:relative}' +
      '.cart__discount--syncing .cart__discount__input-wrapper{position:relative}' +
      '.cart__discount--syncing .cart__discount__input-wrapper::after{' +
      'content:"";position:absolute;right:88px;top:50%;width:18px;height:18px;margin-top:-9px;' +
      'border:2px solid rgba(162,29,53,.25);border-top-color:#a21d35;border-radius:50%;' +
      'animation:ck-discount-spin .65s linear infinite;pointer-events:none;z-index:2}' +
      '@keyframes ck-discount-spin{to{transform:rotate(360deg)}}';
    document.head.appendChild(style);
  }

  function isCartDrawerOpen() {
    try {
      const drawer = document.querySelector('cart-drawer');
      return !!(drawer && drawer.classList.contains('active'));
    } catch (_) {
      return false;
    }
  }

  function showDiscountPillSyncLoader(message) {
    ensureDiscountSyncStyles();
    const msg = message || 'Updating promo…';
    if (isShippingPage()) {
      _discountPillSyncDepth += 1;
      document.querySelectorAll('[data-cart-discount-wrapper]').forEach((wrap) => {
        wrap.classList.add('cart__discount--syncing');
        wrap.setAttribute('aria-busy', 'true');
        const applyBtn = wrap.querySelector('[data-discount-apply]');
        if (applyBtn) applyBtn.disabled = true;
      });
      showSubtotalLoader(msg);
    } else if (isCartDrawerOpen()) {
      showCartDrawerGiftSyncLoader(msg);
    }
  }

  function hideDiscountPillSyncLoader() {
    if (isShippingPage()) {
      if (_discountPillSyncDepth > 0) _discountPillSyncDepth -= 1;
      if (_discountPillSyncDepth > 0) return;
      document.querySelectorAll('[data-cart-discount-wrapper]').forEach((wrap) => {
        wrap.classList.remove('cart__discount--syncing');
        wrap.removeAttribute('aria-busy');
        const applyBtn = wrap.querySelector('[data-discount-apply]');
        if (applyBtn) applyBtn.disabled = false;
      });
      hideSubtotalPriceLoaderOnly();
    } else {
      hideCartDrawerGiftSyncLoader();
    }
  }

  async function waitForFreeGiftTierSync() {
    const mgr = window.freeGiftManager;
    const deadline = Date.now() + 12000;
    while (Date.now() < deadline) {
      if (!mgr) break;
      if (mgr._syncInFlight) {
        try {
          await mgr._syncInFlight;
        } catch (_) {}
        await sleep(80);
        continue;
      }
      const waiting =
        mgr.processing ||
        mgr._externalCartTimer ||
        mgr.debounceTimer ||
        mgr._settleRetryTimer ||
        window.__freeGiftMutationInProgress;
      if (waiting) {
        await sleep(120);
        continue;
      }
      break;
    }
    if (
      isShippingPage() &&
      mgr &&
      typeof mgr.evaluateTierAndSync === 'function' &&
      !mgr._syncInFlight &&
      !mgr.processing
    ) {
      try {
        await mgr.evaluateTierAndSync();
      } catch (_) {}
    }
  }

  function scheduleShippingPillFollowUp() {
    if (!isShippingPage()) return;
    [400, 1000, 1800, 2800].forEach((ms) => {
      setTimeout(async () => {
        try {
          ensureDiscountUiReady();
          const cart = await fetchCart();
          restoreAppliedDiscountPill(cart);
        } catch (_) {
          restoreAppliedDiscountPill(null);
        }
      }, ms);
    });
  }

  /** Shipping page — wait for free-gift, then repaint pills (with follow-up for section lag). */
  async function restoreDiscountPillsAfterGiftSync(cartData) {
    showDiscountPillSyncLoader('Updating promo…');
    try {
      await waitForFreeGiftTierSync();
      ensureDiscountUiReady();
      let cart = cartData;
      if (!cart || !Array.isArray(cart.items)) {
        cart = await fetchCart();
      }
      if (cart) {
        updateCartUI(cart);
        restoreAppliedDiscountPill(cart);
      } else {
        restoreAppliedDiscountPill(null);
      }
      scheduleShippingPillFollowUp();
    } catch (_) {
      restoreAppliedDiscountPill(null);
    } finally {
      hideDiscountPillSyncLoader();
    }
  }

  async function runShippingPillRestoreFromIdle() {
    if (_pillRestoreInFlight) {
      _pillRestoreQueued = true;
      return;
    }
    _pillRestoreInFlight = restoreDiscountPillsAfterGiftSync(null).finally(() => {
      _pillRestoreInFlight = null;
      if (_pillRestoreQueued) {
        _pillRestoreQueued = false;
        void runShippingPillRestoreFromIdle();
      }
    });
    await _pillRestoreInFlight;
  }

  /** Cart drawer — lightweight pill restore (no overlay, no forced gift sync). */
  async function restoreDiscountPillsLight(cartData) {
    ensureDiscountUiReady();
    try {
      let cart = cartData;
      if (!cart || !Array.isArray(cart.discount_codes)) {
        cart = await fetchCart();
      }
      if (cart) {
        updateCartUI(cart);
        restoreAppliedDiscountPill(cart);
      } else {
        restoreAppliedDiscountPill(null);
      }
    } catch (_) {
      restoreAppliedDiscountPill(null);
    } finally {
      hideCartDrawerGiftSyncLoader();
    }
  }

  async function restoreCartDrawerAfterGiftSync(cartData) {
    if (!isCartDrawerOpen()) {
      hideCartDrawerGiftSyncLoader();
      return restoreDiscountPillsLight(cartData);
    }
    showCartDrawerGiftSyncLoader('Updating your free gift…');
    try {
      await waitForFreeGiftTierSync();
      ensureDiscountUiReady();
      let cart = cartData;
      if (!cart || !Array.isArray(cart.items)) {
        cart = await fetchCart();
      }
      if (cart) {
        updateCartUI(cart);
        restoreAppliedDiscountPill(cart);
      } else {
        restoreAppliedDiscountPill(null);
      }
    } catch (_) {
      restoreAppliedDiscountPill(null);
    } finally {
      hideCartDrawerGiftSyncLoader();
    }
  }

  function scheduleRestoreDiscountPills(cartData) {
    if (isShippingPage()) {
      /* Shipping page: silent pill restore by default. Full "Updating promo/free gift"
         loader only when free-gift actually mutates (sync-start / sync-idle mutated). */
      restoreShippingDiscountPillsImmediate(cartData);
      if (_pillRestoreTimer) clearTimeout(_pillRestoreTimer);
      _pillRestoreTimer = setTimeout(() => {
        _pillRestoreTimer = null;
        void restoreDiscountPillsLight(cartData);
        scheduleShippingPillFollowUp();
      }, 180);
      return;
    }
    if (_pillRestoreTimer) clearTimeout(_pillRestoreTimer);
    const delay = 100;
    _pillRestoreTimer = setTimeout(() => {
      _pillRestoreTimer = null;
      if (isCartDrawerOpen()) {
        if (_pillRestoreInFlight) {
          _pillRestoreQueued = true;
          return;
        }
        _pillRestoreInFlight = restoreCartDrawerAfterGiftSync(cartData).finally(() => {
          _pillRestoreInFlight = null;
          if (_pillRestoreQueued) {
            _pillRestoreQueued = false;
            scheduleRestoreDiscountPills(null);
          }
        });
      } else {
        void restoreDiscountPillsLight(cartData);
      }
    }, delay);
  }

  /* ==========================================================================
     DOUBLE-INIT GUARD  (Intelligems may re-inject the script)
  ========================================================================== */
  if (typeof window !== 'undefined') {
    if (window.__tbsDiscountScriptInitialized) return;
    window.__tbsDiscountScriptInitialized = true;
  }

  /* ==========================================================================
     PERSISTENCE  (localStorage + sessionStorage for maximum survival)
  ========================================================================== */
  function persistCode(code) {
    if (!code) return;
    try {
      localStorage.setItem(STORAGE_KEY, code.toUpperCase());
      sessionStorage.setItem(STORAGE_KEY, code.toUpperCase());
    } catch (e) {}
  }
  function getPersistedCode() {
    try {
      return sessionStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY) || null;
    } catch (e) { return null; }
  }
  function clearPersistedCode() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }
  function setCheckoutFlag(code) {
    try {
      sessionStorage.setItem(CHECKOUT_FLAG_KEY, code.toUpperCase());
    } catch(e){}
  }
  
  function getCheckoutFlag() {
    try {
      return sessionStorage.getItem(CHECKOUT_FLAG_KEY);
    } catch(e){ return null; }
  }
  
  function clearCheckoutFlag() {
    try {
      sessionStorage.removeItem(CHECKOUT_FLAG_KEY);
    } catch(e){}
  }
  /* ==========================================================================
     FLAGS
  ========================================================================== */
  let isRemovingInvalidCodes = false;
  let _reapplyInProgress     = false;
  /** Applicable Shopify code currently on the cart, for the DOM helpers that run without a cart object. */
  let _liveNativeCode        = null;

  /* ==========================================================================
     UTILITIES
  ========================================================================== */
  function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

  function formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      minimumFractionDigits: 2, maximumFractionDigits: 2
    }).format(cents / 100).replace(/\.00\b/, '');
  }

  function isShippingPage() {
    try {
      if (document.body && document.body.classList.contains('template-page-shipping')) return true;
    } catch (e) {}
    const path = (window.location && window.location.pathname) || '';
    return path.indexOf('/pages/shipping') >= 0;
  }

  /** Refresh cart drawer via sections API + renderContents (same as cart-drawer.js) */
  async function refreshCartDrawer() {
    try {
      const res = await fetch('/?sections=cart-drawer,cart-icon-bubble&_=' + Date.now());
      const sections = await res.json();
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents({ sections }, { openDrawer: false });
      }
      /* Section replace wipes discount pills and #express-checkout-loader — restore both. */
      ensureCartDrawerDiscountLoader();
      ensureCartDrawerGiftSyncLoader();
      ensureDiscountUiReady();
      const cart = await fetchCart();
      const settledCart = await applyNativeDiscountFromLinkCookieIfNeeded(cart);
      restoreAppliedDiscountPill(settledCart);
    } catch (e) {}
  }

  /** Refresh shipping page section only (no full reload, no cart:updated to avoid multi-ship blink) */
  async function refreshShippingPageSection() {
    const sp = document.querySelector('shipping-page');
    if (sp && typeof sp.refreshSectionFromServer === 'function') {
      sp._lastRefreshTime = Date.now();
      await sp.refreshSectionFromServer();
    } else {
      window.location.reload();
    }
  }

  async function fetchCart() {
    const res = await fetch('/cart.js?' + Date.now());
    return res.json();
  }

  function getApplicableDiscountCode(cart) {
    if (!cart?.discount_codes?.length) return null;
    const dc = cart.discount_codes.find(d => d.applicable === true);
    return dc ? dc.code : null;
  }

  /* ==========================================================================
     SESSION HELPERS
  ========================================================================== */
  function cartAjaxRoot() {
    try {
      const r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
      return typeof r === 'string' && r.length ? r : '/';
    } catch (e) {
      return '/';
    }
  }

  /** Remove all cart-level discount codes via Ajax API (Shopify does not support DELETE /discount/CODE — that 404s). */
  async function clearCartDiscountCodesAjax() {
    return postCartDiscountAjax('');
  }

  /** Set the cart's discount codes via the Ajax cart API. Empty string clears them all. */
  async function postCartDiscountAjax(value) {
    try {
      const res = await fetch(cartAjaxRoot() + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ discount: value }),
      });
      if (!res.ok) return null;
      try {
        return await res.json();
      } catch (_) {
        return null;
      }
    } catch (e) {
      return null;
    }
  }

  function cartHasApplicableCode(cart, code) {
    if (!cart || !Array.isArray(cart.discount_codes)) return false;
    const wanted = String(code).toLowerCase();
    return cart.discount_codes.some(
      (dc) => dc.applicable === true && String(dc.code).toLowerCase() === wanted
    );
  }

  function cartRejectedCode(cart, code) {
    if (!cart || !Array.isArray(cart.discount_codes)) return false;
    const wanted = String(code).toLowerCase();
    return cart.discount_codes.some(
      (dc) => dc.applicable === false && String(dc.code).toLowerCase() === wanted
    );
  }

  async function clearDiscountFromSession() {
    try {
      await clearCartDiscountCodesAjax();
      await fetch('/checkout?discount=', { method: 'GET', credentials: 'same-origin', redirect: 'manual' });
      await sleep(100);
    } catch (e) {}
  }

  /**
   * `/discount/CODE` only primes the code for checkout — it does not land on the cart,
   * so /cart.js kept reporting no code and every apply failed verification. cart/update.js
   * writes the code straight onto the cart and returns the updated cart to verify against.
   * The redirect endpoint stays as a fallback for stores where update.js rejects `discount`.
   */
  async function applyCodeToSession(code, retries = 3) {
    let cart = await postCartDiscountAjax(code);
    if (cartHasApplicableCode(cart, code)) return { success: true, cart, rejected: false };
    if (cartRejectedCode(cart, code)) return { success: false, cart, rejected: true };

    for (let i = 0; i < retries; i++) {
      try {
        await fetch(`/discount/${encodeURIComponent(code)}`, { method: 'GET', credentials: 'same-origin', redirect: 'manual' });
        await sleep(120 + i * 80);
        cart = await fetchCart();
        if (cartHasApplicableCode(cart, code)) return { success: true, cart, rejected: false };
      } catch (e) {}
    }

    const finalCart = cart || (await fetchCart());
    return { success: false, cart: finalCart, rejected: cartRejectedCode(finalCart, code) };
  }

  /* ==========================================================================
     SVG CONSTANTS
  ========================================================================== */
  const BADGE_SVG_OPEN  = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" focusable="false" role="presentation">';
  const BADGE_SVG_PATHS = '<path d="M9.39937 0H6.82937C6.49325 0.000121142 6.16061 0.0680187 5.85134 0.199633C5.54206 0.331247 5.2625 0.523875 5.02937 0.766L0.419371 5.552C0.146884 5.83505 -0.00367186 6.21371 6.80452e-05 6.60659C0.00380795 6.99946 0.161545 7.37519 0.439371 7.653L3.91937 11.133C4.15093 11.3642 4.46391 11.4954 4.7911 11.4983C5.1183 11.5013 5.43362 11.3759 5.66937 11.149L10.7854 6.223C10.9792 6.0364 11.1334 5.81259 11.2388 5.56499C11.3441 5.31739 11.3984 5.05108 11.3984 4.782V2C11.3984 1.46957 11.1877 0.960859 10.8126 0.585786C10.4375 0.210714 9.9288 0 9.39837 0" fill="white"/><path d="M6.82937 0V-1L6.82901 -1L6.82937 0ZM5.02937 0.766L5.7496 1.45974L5.74973 1.4596L5.02937 0.766ZM0.419371 5.552L-0.300854 4.85826L-0.301048 4.85846L0.419371 5.552ZM0.439371 7.653L1.14648 6.94589L1.14646 6.94587L0.439371 7.653ZM3.91937 11.133L3.21226 11.8401L3.21284 11.8407L3.91937 11.133ZM5.66937 11.149L6.36284 11.8695L6.36298 11.8694L5.66937 11.149ZM10.7854 6.223L10.0919 5.50254L10.0918 5.50264L10.7854 6.223ZM11.3984 4.782H10.3984V4.78204L11.3984 4.782ZM9.39937 0V-1H6.82937V0V1H9.39937V0ZM6.82937 0L6.82901 -1C6.35845 -0.99983 5.89275 -0.904774 5.45976 -0.720514L5.85134 0.199633L6.24291 1.11978C6.42848 1.04081 6.62806 1.00007 6.82973 1L6.82937 0ZM5.85134 0.199633L5.45976 -0.720514C5.02678 -0.536254 4.63539 -0.266575 4.30901 0.0724001L5.02937 0.766L5.74973 1.4596C5.88961 1.31433 6.05735 1.19875 6.24291 1.11978L5.85134 0.199633ZM5.02937 0.766L4.30915 0.07226L-0.300854 4.85826L0.419371 5.552L1.1396 6.24574L5.7496 1.45974L5.02937 0.766ZM0.419371 5.552L-0.301048 4.85846C-0.755194 5.33021 -1.00612 5.96131 -0.999887 6.61611L6.80452e-05 6.60659L1.00002 6.59707C0.998776 6.46611 1.04896 6.33989 1.13979 6.24554L0.419371 5.552ZM6.80452e-05 6.60659L-0.999887 6.61611C-0.993653 7.2709 -0.730759 7.89711 -0.267715 8.36013L0.439371 7.653L1.14646 6.94587C1.05385 6.85327 1.00127 6.72803 1.00002 6.59707L6.80452e-05 6.60659ZM0.439371 7.653L-0.267736 8.36011L3.21226 11.8401L3.91937 11.133L4.62648 10.4259L1.14648 6.94589L0.439371 7.653ZM3.91937 11.133L3.21284 11.8407C3.62965 12.2568 4.19301 12.4929 4.78196 12.4983L4.7911 11.4983L4.80025 10.4984C4.73481 10.4978 4.67221 10.4716 4.6259 10.4253L3.91937 11.133ZM4.7911 11.4983L4.78196 12.4983C5.37091 12.5037 5.93849 12.2779 6.36284 11.8695L5.66937 11.149L4.9759 10.4285C4.92875 10.4739 4.86569 10.499 4.80025 10.4984L4.7911 11.4983ZM5.66937 11.149L6.36298 11.8694L11.479 6.94336L10.7854 6.223L10.0918 5.50264L4.97577 10.4286L5.66937 11.149ZM10.7854 6.223L11.4789 6.94346C11.7697 6.66355 12.001 6.32785 12.159 5.95644L11.2388 5.56499L10.3186 5.17354C10.2659 5.29734 10.1888 5.40924 10.0919 5.50254L10.7854 6.223ZM11.2388 5.56499L12.159 5.95644C12.317 5.58504 12.3984 5.18557 12.3984 4.78196L11.3984 4.782L10.3984 4.78204C10.3984 4.91658 10.3712 5.04974 10.3186 5.17354L11.2388 5.56499ZM11.3984 4.782H12.3984V2H11.3984H10.3984V4.782H11.3984ZM11.3984 2H12.3984C12.3984 1.20435 12.0823 0.441289 11.5197 -0.12132L10.8126 0.585786L10.1055 1.29289C10.293 1.48043 10.3984 1.73478 10.3984 2H11.3984ZM10.8126 0.585786L11.5197 -0.12132C10.9571 -0.68393 10.194 -1 9.39837 -1V0V1C9.66359 1 9.91794 1.10536 10.1055 1.29289L10.8126 0.585786Z" fill="black"';

  function makeBadgeSVG(maskId) {
    return BADGE_SVG_OPEN +
      `<mask id="${maskId}" fill="white"><path d="M9.39937 0H6.82937C6.49325 0.000121142 6.16061 0.0680187 5.85134 0.199633C5.54206 0.331247 5.2625 0.523875 5.02937 0.766L0.419371 5.552C0.146884 5.83505 -0.00367186 6.21371 6.80452e-05 6.60659C0.00380795 6.99946 0.161545 7.37519 0.439371 7.653L3.91937 11.133C4.15093 11.3642 4.46391 11.4954 4.7911 11.4983C5.1183 11.5013 5.43362 11.3759 5.66937 11.149L10.7854 6.223C10.9792 6.0364 11.1334 5.81259 11.2388 5.56499C11.3441 5.31739 11.3984 5.05108 11.3984 4.782V2C11.3984 1.46957 11.1877 0.960859 10.8126 0.585786C10.4375 0.210714 9.9288 0 9.39837 0"/></mask>` +
      BADGE_SVG_PATHS + ` mask="url(#${maskId})"/></svg>`;
  }

  /* ==========================================================================
     INSERT DISCOUNT UI  (idempotent — re-inject after cart-drawer section refresh)
  ========================================================================== */
  function ensureDiscountUiReady() {
    insertDiscountUI();
    ensureDualAppliedPills();
  }

  function insertDiscountUI() {
    const target = document.querySelector(TARGET_SELECTOR);
    if (!target || target.querySelector(WRAPPER_SELECTOR)) return;

    const uid = 'injected-' + Date.now();
    const pg = window.freeGiftSettings && window.freeGiftSettings.promoGift;
    const tier2Threshold = pg && pg.threshold != null && Number(pg.threshold) > 0
      ? Math.round(Number(pg.threshold))
      : 150;
    target.insertAdjacentHTML('afterbegin', `
      <div class="cart__discount" data-cart-discount-wrapper>
        <div class="cart__discount__input-wrapper">
          <label class="visually-hidden" for="cart-discount-code">Promo code</label>
          <input type="text" name="discount" id="cart-discount-code"
            class="cart__discount__input" placeholder="Promo code" aria-label="Promo code"
            autocomplete="off" data-discount-input />
          <button type="button" class="cart__discount__apply-btn" data-discount-apply>Apply</button>
        </div>
        <div class="cart__discount__error" data-discount-error
          data-error-invalid="Code invalid, expired, or cart minimum not met"
          data-error-expired="Code invalid, expired, or cart minimum not met"
          data-error-not-applicable="Code invalid, expired, or cart minimum not met"
          data-error-tier2-not-applicable="Minimum $${tier2Threshold} product purchase required"
          data-error-tier2-threshold="${tier2Threshold}"
          hidden>
          <span data-discount-error-message>Code invalid, expired, or cart minimum not met</span>
        </div>
        <div class="cart__discount__applied-list" data-discount-applied-list>
        <div class="cart__discount__applied" data-discount-applied data-discount-kind="shopify" hidden>
          <div class="cart__discount__applied-content">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <g clip-path="url(#clip-tag-${uid})">
                <mask id="mask-tag-${uid}" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="0" y="0" width="14" height="14">
                  <path d="M0 0H14V14H0V0Z" fill="white"/>
                </mask>
                <g mask="url(#mask-tag-${uid})">
                  <mask id="path-tag-${uid}" fill="white">
                    <path d="M10.5 1.5H7.92999C7.59387 1.50012 7.26123 1.56802 6.95195 1.69963C6.64268 1.83125 6.36312 2.02388 6.12999 2.266L1.51999 7.052C1.2475 7.33505 1.09694 7.71371 1.10068 8.10659C1.10442 8.49946 1.26216 8.87519 1.53999 9.153L5.01999 12.633C5.25155 12.8642 5.56453 12.9954 5.89172 12.9983C6.21892 13.0013 6.53424 12.8759 6.76999 12.649L11.886 7.723C12.0798 7.5364 12.2341 7.31259 12.3394 7.06499C12.4447 6.81739 12.499 6.55108 12.499 6.282V3.5C12.499 2.96957 12.2883 2.46086 11.9132 2.08579C11.5381 1.71071 11.0294 1.5 10.499 1.5"/>
                  </mask>
                  <path d="M10.5 1.5H7.92999C7.59387 1.50012 7.26123 1.56802 6.95195 1.69963C6.64268 1.83125 6.36312 2.02388 6.12999 2.266L1.51999 7.052C1.2475 7.33505 1.09694 7.71371 1.10068 8.10659C1.10442 8.49946 1.26216 8.87519 1.53999 9.153L5.01999 12.633C5.25155 12.8642 5.56453 12.9954 5.89172 12.9983C6.21892 13.0013 6.53424 12.8759 6.76999 12.649L11.886 7.723C12.0798 7.5364 12.2341 7.31259 12.3394 7.06499C12.4447 6.81739 12.499 6.55108 12.499 6.282V3.5C12.499 2.96957 12.2883 2.46086 11.9132 2.08579C11.5381 1.71071 11.0294 1.5 10.499 1.5" fill="white"/>
                  <path d="M7.92999 1.5V0.5L7.92963 0.5L7.92999 1.5ZM6.12999 2.266L6.85021 2.95974L6.85035 2.9596L6.12999 2.266ZM1.51999 7.052L0.799762 6.35826L0.799569 6.35846L1.51999 7.052ZM1.53999 9.153L2.24709 8.44589L2.24707 8.44587L1.53999 9.153ZM5.01999 12.633L4.31288 13.3401L4.31346 13.3407L5.01999 12.633ZM6.76999 12.649L7.46346 13.3695L7.46359 13.3694L6.76999 12.649ZM11.886 7.723L11.1925 7.00254L11.1924 7.00264L11.886 7.723ZM12.499 6.282H11.499V6.28204L12.499 6.282ZM10.5 1.5V0.5H7.92999V1.5V2.5H10.5V1.5ZM7.92999 1.5L7.92963 0.5C7.45906 0.50017 6.99337 0.595226 6.56038 0.779486L6.95195 1.69963L7.34353 2.61978C7.52909 2.54081 7.72868 2.50007 7.93035 2.5L7.92999 1.5ZM6.95195 1.69963L6.56038 0.779486C6.12739 0.963746 5.73601 1.23343 5.40963 1.5724L6.12999 2.266L6.85035 2.9596C6.99023 2.81433 7.15796 2.69875 7.34353 2.61978L6.95195 1.69963ZM6.12999 2.266L5.40976 1.57226L0.799762 6.35826L1.51999 7.052L2.24021 7.74574L6.85021 2.95974L6.12999 2.266ZM1.51999 7.052L0.799569 6.35846C0.345423 6.83021 0.0944966 7.46131 0.10073 8.11611L1.10068 8.10659L2.10064 8.09707C2.09939 7.96611 2.14958 7.83989 2.24041 7.74554L1.51999 7.052ZM1.10068 8.10659L0.10073 8.11611C0.106963 8.7709 0.369857 9.39711 0.832902 9.86013L1.53999 9.153L2.24707 8.44587C2.15446 8.35327 2.10189 8.22803 2.10064 8.09707L1.10068 8.10659ZM1.53999 9.153L0.832881 9.86011L4.31288 13.3401L5.01999 12.633L5.72709 11.9259L2.24709 8.44589L1.53999 9.153ZM5.01999 12.633L4.31346 13.3407C4.73027 13.7568 5.29363 13.9929 5.88258 13.9983L5.89172 12.9983L5.90086 11.9984C5.83542 11.9978 5.77283 11.9716 5.72652 11.9253L5.01999 12.633ZM5.89172 12.9983L5.88258 13.9983C6.47153 14.0037 7.03911 13.7779 7.46346 13.3695L6.76999 12.649L6.07652 11.9285C6.02937 11.9739 5.9663 11.999 5.90086 11.9984L5.89172 12.9983ZM6.76999 12.649L7.46359 13.3694L12.5796 8.44336L11.886 7.723L11.1924 7.00264L6.07638 11.9286L6.76999 12.649ZM11.886 7.723L12.5795 8.44346C12.8703 8.16355 13.1016 7.82785 13.2596 7.45644L12.3394 7.06499L11.4192 6.67354C11.3665 6.79734 11.2894 6.90924 11.1925 7.00254L11.886 7.723ZM12.3394 7.06499L13.2596 7.45644C13.4176 7.08504 13.499 6.68557 13.499 6.28196L12.499 6.282L11.499 6.28204C11.499 6.41658 11.4719 6.54974 11.4192 6.67354L12.3394 7.06499ZM12.499 6.282H13.499V3.5H12.499H11.499V6.282H12.499ZM12.499 3.5H13.499C13.499 2.70435 13.1829 1.94129 12.6203 1.37868L11.9132 2.08579L11.2061 2.79289C11.3936 2.98043 11.499 3.23478 11.499 3.5H12.499ZM11.9132 2.08579L12.6203 1.37868C12.0577 0.81607 11.2946 0.5 10.499 0.5V1.5V2.5C10.7642 2.5 11.0186 2.60536 11.2061 2.79289L11.9132 2.08579Z" fill="white" mask="url(#path-tag-${uid})"/>
                  <path d="M9.49998 5.06301C9.81092 5.06301 10.063 4.81095 10.063 4.50001C10.063 4.18908 9.81092 3.93701 9.49998 3.93701C9.18904 3.93701 8.93698 4.18908 8.93698 4.50001C8.93698 4.81095 9.18904 5.06301 9.49998 5.06301Z" fill="#a21d35"/>
                  <path d="M9.48999 4.48999H9.50999V4.50999H9.48999V4.48999Z" fill="white"/>
                </g>
              </g>
              <defs>
                <clipPath id="clip-tag-${uid}"><rect width="14" height="14" fill="white"/></clipPath>
              </defs>
            </svg>
            <span class="cart__discount__code" data-discount-code-display></span>
          </div>
          <button type="button" class="cart__discount__remove" data-discount-remove aria-label="Remove promo code">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5L15 15" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <div class="cart__discount__applied" data-discount-applied data-discount-kind="tier2" hidden>
          <div class="cart__discount__applied-content">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path d="M10.5 1.5H7.93a3.5 3.5 0 0 0-2.8.77L1.52 7.05a1.5 1.5 0 0 0 .02 2.1L5.02 12.63a1.5 1.5 0 0 0 1.75.02L11.89 7.72a2 2 0 0 0 .61-1.44V3.5a2 2 0 0 0-2-2Z" fill="white"/>
              <circle cx="9.5" cy="4.5" r=".56" fill="#a21d35"/>
            </svg>
            <span class="cart__discount__code" data-discount-code-display></span>
          </div>
          <button type="button" class="cart__discount__remove" data-discount-remove aria-label="Remove gift code">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5L15 15" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        </div>
      </div>`);
  }

  /* ==========================================================================
     LOADER HELPERS
  ========================================================================== */
  /** True while apply/remove owns the cart-drawer overlay (survives section refresh). */
  let _drawerDiscountLoaderActive = false;
  let _drawerDiscountLoaderMessage = 'Updating promo…';
  /** Free-gift add/remove in cart drawer — separate from discount apply (no checkout lock). */
  let _drawerGiftSyncLoaderActive = false;
  let _drawerGiftSyncLoaderMessage = 'Updating your free gift…';

  function showSubtotalLoader() {
    const el = document.querySelector('[data-subtotal-price]');
    if (!el) return;
    const ct = el.closest('.cart__total');
    if (ct) {
      ct.classList.add('is-loading');
      if (!el.classList.contains('cart__total__price--animated'))
        el.classList.add('cart__total__price--animated');
      if (!ct.querySelector('.cart__price__loader')) {
        const ldr = document.createElement('div');
        ldr.className = 'cart__price__loader loader';
        ldr.innerHTML = '<div class="loader-indeterminate"></div>';
        ct.appendChild(ldr);
      }
    }
  }
  function hideSubtotalPriceLoaderOnly() {
    const el = document.querySelector('[data-subtotal-price]');
    if (!el) return;
    const ct = el.closest('.cart__total');
    if (ct) ct.classList.remove('is-loading');
  }
  function hideSubtotalLoader(force) {
    hideCartDrawerDiscountLoader(force);
    hideSubtotalPriceLoaderOnly();
  }

  /**
   * Full-drawer overlay during apply / remove. Reuses #express-checkout-loader so we
   * do not add a second overlay. Flag survives cart-drawer section refresh (renderContents
   * replaces .drawer__inner and would otherwise hide the loader mid-process).
   */
  function showCartDrawerDiscountLoader(message) {
    _drawerDiscountLoaderActive = true;
    if (message) _drawerDiscountLoaderMessage = message;
    const loader = document.getElementById('express-checkout-loader');
    if (!loader) return;
    const msgEl = loader.querySelector('.express-checkout-loader__message');
    if (msgEl) msgEl.textContent = _drawerDiscountLoaderMessage;
    loader.removeAttribute('hidden');
    loader.setAttribute('aria-hidden', 'false');
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.add('discount-code-loading');
  }

  function hideCartDrawerDiscountLoader() {
    _drawerDiscountLoaderActive = false;
    if (!_drawerGiftSyncLoaderActive) {
      const loader = document.getElementById('express-checkout-loader');
      if (loader) {
        loader.setAttribute('hidden', '');
        loader.setAttribute('aria-hidden', 'true');
        const msgEl = loader.querySelector('.express-checkout-loader__message');
        if (msgEl) msgEl.textContent = 'Preparing checkout…';
      }
    }
    document.querySelectorAll('cart-drawer').forEach((drawer) => {
      drawer.classList.remove('discount-code-loading');
    });
  }

  /**
   * Cart drawer overlay while free-gift adds/removes the gift line.
   * Does NOT add discount-code-loading — checkout buttons stay enabled after sync ends.
   */
  function showCartDrawerGiftSyncLoader(message) {
    if (!isCartDrawerOpen()) return;
    _drawerGiftSyncLoaderActive = true;
    if (message) _drawerGiftSyncLoaderMessage = message;
    ensureDiscountSyncStyles();
    const loader = document.getElementById('express-checkout-loader');
    if (loader) {
      const msgEl = loader.querySelector('.express-checkout-loader__message');
      if (msgEl) msgEl.textContent = _drawerGiftSyncLoaderMessage;
      loader.removeAttribute('hidden');
      loader.setAttribute('aria-hidden', 'false');
    }
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.add('gift-sync-loading');
    showSubtotalLoader();
    document.querySelectorAll('cart-drawer [data-cart-discount-wrapper]').forEach((wrap) => {
      wrap.classList.add('cart__discount--syncing');
      wrap.setAttribute('aria-busy', 'true');
      const applyBtn = wrap.querySelector('[data-discount-apply]');
      if (applyBtn) applyBtn.disabled = true;
    });
  }

  function hideCartDrawerGiftSyncLoader() {
    if (!_drawerGiftSyncLoaderActive) return;
    _drawerGiftSyncLoaderActive = false;
    if (!_drawerDiscountLoaderActive) {
      const loader = document.getElementById('express-checkout-loader');
      if (loader) {
        loader.setAttribute('hidden', '');
        loader.setAttribute('aria-hidden', 'true');
        const msgEl = loader.querySelector('.express-checkout-loader__message');
        if (msgEl) msgEl.textContent = 'Preparing checkout…';
      }
      hideSubtotalPriceLoaderOnly();
    }
    document.querySelectorAll('cart-drawer').forEach((drawer) => {
      drawer.classList.remove('gift-sync-loading');
    });
    document.querySelectorAll('cart-drawer [data-cart-discount-wrapper]').forEach((wrap) => {
      wrap.classList.remove('cart__discount--syncing');
      wrap.removeAttribute('aria-busy');
      const applyBtn = wrap.querySelector('[data-discount-apply]');
      if (applyBtn) applyBtn.disabled = false;
    });
  }

  /** Re-paint gift-sync overlay after drawer section refresh while sync is still running. */
  function ensureCartDrawerGiftSyncLoader() {
    if (!_drawerGiftSyncLoaderActive) return;
    showCartDrawerGiftSyncLoader(_drawerGiftSyncLoaderMessage);
  }

  /** Re-paint the overlay after a drawer section refresh while apply/remove is still running. */
  function ensureCartDrawerDiscountLoader() {
    if (!_drawerDiscountLoaderActive) return;
    showCartDrawerDiscountLoader(_drawerDiscountLoaderMessage);
  }

  /* ==========================================================================
     UI STATE HELPERS
  ========================================================================== */
  /**
   * Shopify returns the same signal for an unknown code and a real code the cart does not
   * qualify for (deliberate, to stop code guessing), so one message has to cover both.
   */
  function rejectedCodeMessage() {
    const holder = document.querySelector('[data-discount-error][data-error-invalid]');
    return (holder && holder.getAttribute('data-error-invalid')) || 'Invalid promo code';
  }

  function showError(msg) {
    document.querySelectorAll('[data-discount-error]').forEach((div) => {
      const span = div.querySelector('[data-discount-error-message]');
      const input = div.closest('[data-cart-discount-wrapper]')?.querySelector('[data-discount-input]');
      if (span) span.textContent = msg;
      div.removeAttribute('hidden');
      if (input) {
        input.classList.add('has-error');
        if (window.innerWidth <= 749) input.classList.add('has-error-mobile');
        input.setAttribute('aria-invalid', 'true');
      }
      div.setAttribute('role', 'alert');
    });
  }
  function hideError() {
    document.querySelectorAll('[data-discount-error]').forEach((div) => {
      const span = div.querySelector('[data-discount-error-message]');
      const input = div.closest('[data-cart-discount-wrapper]')?.querySelector('[data-discount-input]');
      if (span) span.textContent = '';
      div.setAttribute('hidden', '');
      if (input) {
        input.classList.remove('has-error', 'has-error-mobile');
        input.removeAttribute('aria-invalid');
      }
      div.removeAttribute('role');
    });
  }

  function uniquifySvgIds(root, suffix) {
    if (!root || !suffix) return;
    const idMap = {};
    root.querySelectorAll('[id]').forEach((el) => {
      const oldId = el.id;
      if (!oldId) return;
      const neu = oldId + '-' + suffix;
      idMap[oldId] = neu;
      el.id = neu;
    });
    const attrs = ['clip-path', 'mask', 'fill', 'stroke', 'filter', 'href', 'xlink:href'];
    root.querySelectorAll('*').forEach((el) => {
      attrs.forEach((attr) => {
        const v = el.getAttribute(attr);
        if (!v || v.indexOf('url(#') === -1) return;
        let next = v;
        Object.keys(idMap).forEach((oldId) => {
          next = next.split('url(#' + oldId + ')').join('url(#' + idMap[oldId] + ')');
        });
        if (next !== v) el.setAttribute(attr, next);
      });
    });
  }

  function ensureDualAppliedPills() {
    document.querySelectorAll('[data-cart-discount-wrapper]').forEach((wrapper) => {
      let list = wrapper.querySelector('[data-discount-applied-list]');
      const pills = Array.from(wrapper.querySelectorAll('[data-discount-applied]'));
      if (!list) {
        list = document.createElement('div');
        list.className = 'cart__discount__applied-list';
        list.setAttribute('data-discount-applied-list', '');
        if (pills.length) {
          pills[0].parentNode.insertBefore(list, pills[0]);
          pills.forEach((p) => list.appendChild(p));
        } else {
          wrapper.appendChild(list);
        }
      }

      let shopify = list.querySelector('[data-discount-kind="shopify"]');
      let tier2 = list.querySelector('[data-discount-kind="tier2"]');
      list.querySelectorAll('[data-discount-applied]:not([data-discount-kind])').forEach((el) => {
        if (!shopify) {
          el.setAttribute('data-discount-kind', 'shopify');
          shopify = el;
        } else if (!tier2) {
          el.setAttribute('data-discount-kind', 'tier2');
          tier2 = el;
        }
      });

      if (!shopify && list.querySelector('[data-discount-applied]')) {
        shopify = list.querySelector('[data-discount-applied]');
        shopify.setAttribute('data-discount-kind', 'shopify');
      }

      if (!tier2 && shopify) {
        tier2 = shopify.cloneNode(true);
        tier2.setAttribute('data-discount-kind', 'tier2');
        tier2.setAttribute('hidden', '');
        const display = tier2.querySelector('[data-discount-code-display]');
        if (display) display.textContent = '';
        const removeBtn = tier2.querySelector('[data-discount-remove]');
        if (removeBtn) removeBtn.setAttribute('aria-label', 'Remove gift code');
        uniquifySvgIds(tier2, 't2-' + Math.random().toString(36).slice(2, 8));
        list.appendChild(tier2);
      }
    });
  }

  function showAppliedKind(kind, code) {
    if (!code) return;
    ensureDualAppliedPills();
    document.querySelectorAll('[data-cart-discount-wrapper]').forEach((wrapper) => {
      const block = wrapper.querySelector('[data-discount-applied][data-discount-kind="' + kind + '"]');
      const display = block && block.querySelector('[data-discount-code-display]');
      const input = wrapper.querySelector('[data-discount-input]');
      if (block && display) {
        block.removeAttribute('hidden');
        display.textContent = String(code).toUpperCase();
        if (input) input.value = '';
      }
    });
  }

  function hideAppliedKind(kind) {
    document.querySelectorAll('[data-cart-discount-wrapper]').forEach((wrapper) => {
      const block = wrapper.querySelector('[data-discount-applied][data-discount-kind="' + kind + '"]');
      const display = block && block.querySelector('[data-discount-code-display]');
      if (block) {
        block.setAttribute('hidden', '');
        if (display) display.textContent = '';
      }
    });
  }

  function showApplied(code) {
    showAppliedKind('shopify', code);
  }
  /**
   * Hide native Shopify discount pill — keep Tier 2 theme pill if still stored.
   */
  function hideApplied() {
    hideAppliedKind('shopify');
    const tier2Theme = getAppliedTier2ThemeCode();
    if (tier2Theme) {
      showAppliedKind('tier2', tier2Theme);
    } else {
      hideAppliedKind('tier2');
    }
  }
  /** Force-hide both pills (after Tier 2 storage already cleared — user remove / empty cart / free-gift clear). */
  function hideAppliedForced() {
    hideAppliedKind('shopify');
    hideAppliedKind('tier2');
  }
  function wipeLineBadges() {
    document.querySelectorAll('.cart__item__discount-line').forEach(el => el.remove());
  }

  /* ==========================================================================
     BADGE BUILDER  — creates one .cart__item__discount-line element
  ========================================================================== */
  function buildBadge(itemKey, allocIndex, label, amount) {
    const maskId = `discount-mask-${itemKey.replace(/[^a-zA-Z0-9]/g, '-')}-${allocIndex}-${Date.now()}`;
    const line   = document.createElement('p');
    line.className = 'cart__item__discount-line';

    const badge = document.createElement('span');
    badge.className = 'cart-item-discount-badge';
    badge.innerHTML = makeBadgeSVG(maskId);

    const title = document.createElement('span');
    title.className = 'cart-item-discount-title';
    title.textContent = `${label} (-${formatMoney(amount)})`;

    line.appendChild(badge);
    line.appendChild(title);
    return line;
  }

  function insertBadgeAfterTitle(container, badgeEl) {
    const titleEl = container.querySelector('.cart__item__title');
    if (titleEl && titleEl.parentNode) {
      titleEl.nextSibling
        ? titleEl.parentNode.insertBefore(badgeEl, titleEl.nextSibling)
        : titleEl.parentNode.appendChild(badgeEl);
    } else {
      container.insertBefore(badgeEl, container.firstChild);
    }
  }

  /* ==========================================================================
     UPDATE CART UI
     ─ The ONLY function that writes to the DOM.
     ─ Receives a (possibly pre-cleaned) cart object.
     ─ Determines discount type ONCE at the top:
         • ORDER-level  → cart_level_discount_applications present
                          → hide all line badges, update subtotal only
         • LINE-level   → allocations with type='discount_code' present
                          → show badges per line item
  ========================================================================== */
  function updateCartUI(cart) {
    hideSubtotalPriceLoaderOnly();
    ensureDiscountUiReady();

    /* ── EMPTY CART ── */
    if (!cart.item_count) {
      if (getPersistedCode()) {
        clearPersistedCode();
        clearDiscountFromSession().catch(() => {});
      }
      _liveNativeCode = null;
      const core = getFreeGiftCore();
      if (core && typeof core.clearAppliedTier2Code === 'function') {
        core.clearAppliedTier2Code();
      } else {
        try {
          sessionStorage.removeItem('ck_tier2_gift_code');
          localStorage.removeItem('ck_tier2_gift_code');
        } catch (e) {}
      }
      wipeLineBadges();
      hideAppliedForced();
      hideError();
      const el = document.querySelector('[data-discount-input]');
      if (el) el.value = '';
      const sub = document.querySelector('[data-subtotal-price]') || document.querySelector('[data-cart-price-holder]');
      if (sub) sub.innerHTML = formatMoney(0);
      return;
    }

    _liveNativeCode = getApplicableDiscountCode(cart);

    /* ── SUBTOTAL ── */
    const sub = document.querySelector('[data-subtotal-price]') || document.querySelector('[data-cart-price-holder]');
    if (sub) sub.innerHTML = formatMoney(cart.total_price);

    /* ── DISCOUNT ROW (before subtotal / estimated total) ── */
    const totalDiscount = cart.total_discount || 0;
    document.querySelectorAll('[data-cart-discount-row]').forEach((row) => {
      const amountEl = row.querySelector('[data-cart-discount-amount]');
      if (totalDiscount > 0 && amountEl) {
        row.style.display = '';
        row.removeAttribute('hidden');
        amountEl.textContent = '-' + formatMoney(totalDiscount);
      } else {
        row.style.display = 'none';
        row.setAttribute('hidden', '');
      }
    });

    /* ── INVALID CODE HANDLING ── */
    const invalidCodes = (cart.discount_codes || []).filter(dc => dc.applicable === false);
    const validNativeCodes = (cart.discount_codes || []).filter(dc => dc.applicable === true);
    if (invalidCodes.length > 0 && !isRemovingInvalidCodes) {
      /* Valid code still on cart (e.g. ENJOY) + failed second code → keep ENJOY.
         Never clearCartDiscountCodesAjax here or the good code is wiped too. */
      if (validNativeCodes.length > 0) {
        restoreAppliedDiscountPill(
          Object.assign({}, cart, { discount_codes: validNativeCodes })
        );
      } else {
        isRemovingInvalidCodes = true;
        const input = document.querySelector('[data-discount-input]');
        if (input) {
          const v = input.value.trim().toLowerCase();
          if (invalidCodes.some(dc => dc.code.toLowerCase() === v)) input.value = '';
        }
        clearPersistedCode();
        wipeLineBadges();
        // Do not wipe Tier 2 theme pill — only clear native persisted codes above.
        // hideApplied() re-shows Tier 2 if ck_tier2_gift_code is still set.
        hideApplied();
        clearCartDiscountCodesAjax()
          .then(() => {
            setTimeout(() => {
              isRemovingInvalidCodes = false;
            }, 2000);
            return refreshCart(false);
          })
          .catch(() => {
            setTimeout(() => {
              isRemovingInvalidCodes = false;
            }, 2000);
          });
        restoreAppliedDiscountPill(cart);
        return;
      }
    }

    /* ── DISCOUNT TYPE DETECTION ──
       Order-level  = cart_level_discount_applications has entries
                      (Shopify applies these to the order total, not per-line)
       Line-level   = discount_allocations on items with type = 'discount_code'
    */
    const isOrderLevelDiscount =
      Array.isArray(cart.cart_level_discount_applications) &&
      cart.cart_level_discount_applications.length > 0;

    /* Build a Set of ONLY valid applicable code strings (lowercase) */
    const activeCodes = new Set(
      (cart.discount_codes || [])
        .filter(dc => dc.applicable === true)
        .map(dc => dc.code.toLowerCase())
    );

    /* ── PER-ITEM LOOP ── */
    cart.items.forEach(item => {
      /* Find the row DOM element — try multiple strategies */
      let row = document.querySelector(`[data-item="${item.key}"]`);
      if (!row) {
        const ctx = document.querySelector('cart-drawer, [data-cart-drawer], .drawer__items, [data-items-holder], .cart, [data-cart-items], cart-items');
        if (ctx) row = ctx.querySelector(`[data-item="${item.key}"]`);
      }
      if (!row) {
        document.querySelectorAll('[data-item]').forEach(el => {
          if (el.getAttribute('data-item') === item.key) row = el;
        });
      }
      if (!row) return;

      const originalLine = item.original_line_price;
      const finalLine    = item.line_price;
      const originalUnit = item.original_price;
      const finalUnit    = item.price;
      const hasDiscount  = originalUnit > finalUnit || originalLine > finalLine;

      /* Unit price */
      let priceEl = row.querySelector('.cart__item-price--sale') || row.querySelector('.cart__item-price--regular');
      if (!priceEl) {
        const pc = row.querySelector('.cart__item-prices');
        if (pc) priceEl = pc.querySelector('.cart__item-price--sale, .cart__item-price--regular');
      }
      if (priceEl) {
        priceEl.innerHTML = formatMoney(finalUnit);
        if (hasDiscount && priceEl.classList.contains('cart__item-price--regular')) {
          priceEl.classList.replace('cart__item-price--regular', 'cart__item-price--sale');
        }
      }

      /* Compare-at price */
      const compareEl = row.querySelector('.cart__item-price--compare');
      if (compareEl) {
        if (hasDiscount) {
          compareEl.innerHTML    = formatMoney(originalUnit);
          compareEl.style.display = 'inline-block';
        } else {
          compareEl.style.display = 'none';
        }
      }

      /* Line total */
      row.querySelectorAll('.cart__price').forEach(el => {
        let html = hasDiscount
          ? `<ins>${formatMoney(finalLine)}</ins><del>${formatMoney(originalLine)}</del>`
          : formatMoney(finalLine);
        const lp = el.querySelector('.line__price');
        if (lp) html += lp.outerHTML;
        el.innerHTML = html;
      });

      /* ── LINE-ITEM DISCOUNT BADGES ── */
      const inner = row.querySelector('.cart__item__content-inner');
      if (!inner) return;

      /* Preserve any title already rendered by Liquid before we wipe badges */
      let existingTitle = null;
      inner.querySelectorAll('.cart__item__discount-line').forEach(line => {
        const ts = line.querySelector('.cart-item-discount-title');
        if (ts) {
          const m = ts.textContent.trim().match(/^([^(]+)/);
          if (m) {
            const t = m[1].trim();
            if (t && t.toLowerCase() !== 'automatic discount') existingTitle = t;
          }
        }
      });

      /* Always wipe stale badges before re-rendering */
      inner.querySelectorAll('.cart__item__discount-line').forEach(l => l.remove());

      /*
        RULE:
        - ORDER-level discount  → NO badges on line items whatsoever
        - LINE-level discount   → Badge only for allocations where:
            1. discount_application.type === 'discount_code'  (not automatic/script)
            2. The code is in activeCodes (applicable === true)
            3. The code is NOT in invalidCodes
      */
      if (isOrderLevelDiscount) {
        /* Order discount: no per-line badges */
        return;
      }

      const hasAllocations = Array.isArray(item.discount_allocations) && item.discount_allocations.length > 0;

      if (hasAllocations) {
        item.discount_allocations.forEach((alloc, allocIndex) => {
          const app          = alloc.discount_application;
          const hasApp       = app != null;
          const hasCode      = hasApp && !!app.code;
          const discountType = hasApp ? app.type : null;

          /* Skip automatic / script / order-level allocations */
          const isAutomatic = !hasApp || !hasCode ||
                               discountType === 'automatic' ||
                               discountType === 'script';
          if (isAutomatic) return;

          const allocCode = app.code.toLowerCase();

          /* Skip if not in the active applicable set */
          if (!activeCodes.has(allocCode)) return;

          /* Skip if this code is in the invalid list */
          const isInvalid = invalidCodes.some(dc => dc.code.toLowerCase() === allocCode);
          if (isInvalid) return;

          /* Determine label:
             Priority 1 — existing Liquid-rendered title (what was already on page)
             Priority 2 — app.title from Shopify API
             Priority 3 — the code string itself (uppercase) */
          let label = existingTitle ||
                      (app.title && app.title.trim() && app.title.trim().toLowerCase() !== 'automatic discount'
                        ? app.title.trim()
                        : null) ||
                      app.code.toUpperCase();

          /* Final safety: if label equals the discount code, verify it's active */
          if (!activeCodes.has(label.toLowerCase()) && !activeCodes.has(allocCode)) return;

          const badgeEl = buildBadge(item.key, allocIndex, label, alloc.amount);
          insertBadgeAfterTitle(inner, badgeEl);
        });

      } else if (originalLine > finalLine) {
        /*
          Fallback: price difference but no allocations.
          Only show a badge if there is a valid applicable CODE-based discount
          (NOT automatic — those are shown in the totals area by Liquid).
        */
        const applicableCode = getApplicableDiscountCode(cart);
        if (applicableCode && activeCodes.has(applicableCode.toLowerCase())) {
          const badgeEl = buildBadge(item.key, 0, applicableCode.toUpperCase(), originalLine - finalLine);
          insertBadgeAfterTitle(inner, badgeEl);
        }
      }
    }); /* end cart.items.forEach */

    restoreAppliedDiscountPill(cart);
  }

  /* ==========================================================================
     REFRESH CART
  ========================================================================== */
  async function refreshCart(forceRebuild = false) {
    try {
      showSubtotalLoader('Updating cart…');
      if (forceRebuild) {
        const ci = document.querySelector('cart-items');
        if (ci && typeof ci.getCart === 'function') {
          ci.getCart();
          await sleep(500);
        }
      }
      const cart = await fetchCart();
      await sleep(forceRebuild ? 100 : 30);
      const settledCart = await applyNativeDiscountFromLinkCookieIfNeeded(cart);
      updateCartUI(settledCart);
      return settledCart;
    } catch (e) {
      return null;
    } finally {
      hideSubtotalLoader(true);
    }
  }

  /* ==========================================================================
     REAPPLY ON RETURN  (back button / tab focus / drawer open)
     ─ Uses a mutex to prevent concurrent calls flickering stale codes.
     ─ Before rendering, strips any discount_codes that don't match the
       live Shopify code — prevents numeric IDs leaking as labels.
  ========================================================================== */
  async function reapplyOnReturn() {
    if (_reapplyInProgress) return;
    _reapplyInProgress = true;
    try {
      /* Shipping page tab focus: restore pills only. Full re-apply / free-gift sync flashes
         the shipping-page / promo loader on every tab switch. */
      if (isShippingPage()) {
        ensureDiscountUiReady();
        try {
          const cart = await fetchCart();
          restoreAppliedDiscountPill(cart || null);
        } catch (_) {
          restoreAppliedDiscountPill(null);
        }
        return;
      }

      const tier2Theme = getAppliedTier2ThemeCode();
      if (tier2Theme && !_liveNativeCode) {
        showTier2ThemeApplied(tier2Theme);
      }

      const cart = await fetchCart();

      /* Empty cart — reset everything */
      if (!cart.item_count) {
        _liveNativeCode = null;
        clearPersistedCode();
        const core = getFreeGiftCore();
        if (core && typeof core.clearAppliedTier2Code === 'function') {
          core.clearAppliedTier2Code();
        } else {
          try {
            sessionStorage.removeItem('ck_tier2_gift_code');
            localStorage.removeItem('ck_tier2_gift_code');
          } catch (e) {}
        }
        wipeLineBadges();
        hideApplied();
        clearDiscountFromSession().catch(() => {});
        return;
      }

      let cartForReapply = await applyNativeDiscountFromLinkCookieIfNeeded(cart);

      const liveCode  = getApplicableDiscountCode(cartForReapply);
      const savedCode = getPersistedCode();

      if (liveCode) {
        clearCheckoutFlag();
        /* Shopify has a live code — clean the cart object before rendering
           to remove any OTHER stale codes (e.g. old order-level codes) */
        const cleanCart = Object.assign({}, cartForReapply, {
          discount_codes: (cartForReapply.discount_codes || []).filter(
            dc => dc.code.toLowerCase() === liveCode.toLowerCase()
          )
        });
        persistCode(liveCode);
        updateCartUI(cleanCart);
        restoreAppliedDiscountPill(cleanCart);

      } else if (savedCode) {
        const checkoutCode = getCheckoutFlag();

        /* ----------------------------------------------------------
           USER REMOVED DISCOUNT AT CHECKOUT
        ---------------------------------------------------------- */
        if (checkoutCode && checkoutCode.toLowerCase() === savedCode.toLowerCase()) {
          _liveNativeCode = null;
          clearPersistedCode();
          clearCheckoutFlag();

          wipeLineBadges();
          hideApplied();
          updateCartUI(cart);

          return;
        }
        /* Session was reset (back-nav) — wipe stale DOM immediately, then re-apply */
        _liveNativeCode = null;
        wipeLineBadges();
        hideApplied();
        const { success, cart: updatedCart } = await applyCodeToSession(savedCode, 3);
        if (success) {
          _liveNativeCode = savedCode;
          let settledCart = updatedCart;
          if (await syncFreeGiftAfterDiscountChange()) {
            try { settledCart = await fetchCart(); } catch (_) {}
          }
          updateCartUI(settledCart);
          showApplied(savedCode);
        } else {
          clearPersistedCode();
          updateCartUI(cart);
          hideApplied();
        }

      } else {
        /* No code anywhere — clean slate */
        _liveNativeCode = null;
        wipeLineBadges();
        hideApplied();
        updateCartUI(cart);
      }
    } finally {
      _reapplyInProgress = false;
    }
  }

  function bindVisibilityReapply() {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') reapplyOnReturn();
    });
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) reapplyOnReturn();
    });
    /* Skip window focus on shipping page — it fires on every click into the document
       and was stacking promo/gift loaders with visibilitychange. */
    window.addEventListener('focus', () => {
      if (isShippingPage()) return;
      reapplyOnReturn();
    });
  }

  /* ==========================================================================
     CHECKOUT INTERCEPT
     ─ No saved code → let browser navigate normally (never adds ?discount=)
     ─ Has saved code → re-apply, then redirect with ?discount=CODE
  ========================================================================== */
  function interceptCheckout() {
    document.body.addEventListener('click', async function (e) {
      const btn = e.target.closest('[name="checkout"], .cart__checkout, a[href*="/checkout"]');
      if (!btn) return;

      const code = getPersistedCode();
      if (!code) {
        /* No code — ensure session is also clean, then let click proceed.
           Skip when the cart itself holds a code (applied in another tab or via a
           /discount link); clearing would strip a discount the customer still has. */
        if (!_liveNativeCode) clearDiscountFromSession().catch(() => {});
        return;
      }

      e.preventDefault();
      e.stopImmediatePropagation();
      const orig = btn.textContent;
      btn.textContent = 'Loading…';
      btn.disabled = true;

      try {
        const { success } = await applyCodeToSession(code, 3);
        if (success) {
          setCheckoutFlag(code);
          window.location.href = `/checkout?discount=${encodeURIComponent(code.toUpperCase())}`;
        } else {
          clearPersistedCode();
          hideApplied();
          wipeLineBadges();
          window.location.href = '/checkout';
        }
      } catch (err) {
        window.location.href = `/checkout?discount=${encodeURIComponent(code.toUpperCase())}`;
      } finally {
        btn.textContent = orig;
        btn.disabled    = false;
      }
    }, true);
  }

  /* ==========================================================================
     APPLY DISCOUNT  (user action)
  ========================================================================== */
  async function applyDiscount(clickedBtn) {
    const wrapper = clickedBtn ? clickedBtn.closest('[data-cart-discount-wrapper]') : null;
    const input   = wrapper ? wrapper.querySelector('[data-discount-input]') : document.querySelector('[data-discount-input]');
    const applyBtn = clickedBtn || document.querySelector('[data-discount-apply]');
    const code     = (input && input.value.trim()) ? input.value.trim().toUpperCase() : '';

    if (!code) { showError('Enter a promo code'); return; }

    /* ── Tier 2 theme-only gift code (no native Shopify discount) ── */
    if (isTier2ThemeGiftCode(code)) {
      hideError();
      if (applyBtn) { applyBtn.disabled = true; applyBtn.textContent = 'Applying…'; }
      showSubtotalLoader();
      showCartDrawerDiscountLoader('Applying promo code');
      try {
        const meetsThreshold = await cartMeetsTier2Threshold();
        if (!meetsThreshold) {
          showError(tier2ThresholdErrorMessage());
          return;
        }

        const existing = getAppliedTier2ThemeCode();
        if (existing && existing.toUpperCase() === code) {
          /* Same Tier 2 code re-entered — still drop any Shopify promo (one code only). */
          const sameCart = await fetchCart();
          const sameNative = getApplicableDiscountCode(sameCart);
          if (sameNative) {
            _liveNativeCode = null;
            clearPersistedCode();
            hideAppliedKind('shopify');
            wipeLineBadges();
            await clearDiscountFromSession();
          }
          showTier2ThemeApplied(code);
          if (sameNative) {
            await notifyFreeGiftSync();
            if (isShippingPage()) {
              await sleep(200);
              await Promise.all([refreshShippingPageSection(), refreshCartDrawer()]);
            } else {
              await refreshCartDrawer();
            }
          }
          return;
        }

        /* One promo at a time: Tier 2 replaces any active Shopify discount code. */
        const currentCart = await fetchCart();
        const existingNative = getApplicableDiscountCode(currentCart);
        if (existingNative) {
          _liveNativeCode = null;
          clearPersistedCode();
          hideAppliedKind('shopify');
          wipeLineBadges();
          await clearDiscountFromSession();
        }

        setAppliedTier2ThemeCode(code);
        const core = getFreeGiftCore();
        if (core && typeof core.clearPendingTier2Code === 'function') {
          core.clearPendingTier2Code();
        }
        showTier2ThemeApplied(code);
        if (input) input.value = '';
        await notifyTier2GiftSync();
        if (isShippingPage()) {
          await sleep(200);
          await Promise.all([refreshShippingPageSection(), refreshCartDrawer()]);
        } else {
          await refreshCartDrawer();
        }
      } catch (err) {
        showError('Please check the promo code and try again');
      } finally {
        hideCartDrawerDiscountLoader();
        if (applyBtn) { applyBtn.disabled = false; applyBtn.textContent = 'Apply'; }
      }
      return;
    }

    hideError();
    if (applyBtn) { applyBtn.disabled = true; applyBtn.textContent = 'Applying…'; }
    showSubtotalLoader();
    showCartDrawerDiscountLoader('Applying promo code');

    let previousCode = null;
    let previousTier2 = null;

    try {
      const currentCart  = await fetchCart();
      const existingCode = getApplicableDiscountCode(currentCart);

      if (existingCode && existingCode.toLowerCase() === code.toLowerCase()) {
        _liveNativeCode = existingCode;
        showApplied(existingCode);
        persistCode(existingCode);
        /* One promo at a time: keep Shopify code, drop Tier 2 if it was also active. */
        if (getAppliedTier2ThemeCode()) {
          clearAppliedTier2ThemeCode();
          hideAppliedKind('tier2');
          await notifyTier2GiftSync();
          if (isShippingPage()) {
            await sleep(200);
            await Promise.all([refreshShippingPageSection(), refreshCartDrawer()]);
          } else {
            await refreshCartDrawer();
          }
        }
        return;
      }

      if (existingCode) {
        previousCode = existingCode;
        _liveNativeCode = null;
        hideApplied();       /* clear old pill immediately — no stale flash */
        wipeLineBadges();    /* clear old line badges immediately */
        await clearDiscountFromSession();
      }

      /* One promo at a time: clear Tier 2 before applying a Shopify code; restore on failure. */
      previousTier2 = getAppliedTier2ThemeCode();
      if (previousTier2) {
        clearAppliedTier2ThemeCode();
        hideAppliedKind('tier2');
        await notifyTier2GiftSync();
      }

      const { success, cart } = await applyCodeToSession(code, 3);

      if (success) {
        previousTier2 = null; /* committed — do not restore Tier 2 */
        _liveNativeCode = code;
        persistCode(code);
        hideError();
        /* Update UI immediately for instant feedback, then refresh in background */
        updateCartUI(cart);
        showApplied(code);
        hideAppliedKind('tier2');

        /* Re-check gift eligibility before repainting — the code may have pushed the
           merchandise total below a tier, so the gift line has to go first. */
        let settledCart = cart;
        if (await syncFreeGiftAfterDiscountChange()) {
          try { settledCart = await fetchCart(); } catch (_) {}
        }

        if (isShippingPage()) {
          await sleep(200);
          await notifyFreeGiftSync();
          await Promise.all([refreshShippingPageSection(), refreshCartDrawer()]);
          updateCartUI(settledCart);
          showApplied(code);
          hideAppliedKind('tier2');
          return;
        }
        await notifyFreeGiftSync();
        await refreshCartDrawer();
        updateCartUI(settledCart);
        hideAppliedKind('tier2');
      } else {
        showError(rejectedCodeMessage());
        if (input) input.value = code;

        if (previousCode) {
          const { success: r, cart: rc } = await applyCodeToSession(previousCode, 3);
          if (r) {
            _liveNativeCode = previousCode;
            updateCartUI(rc);
            showApplied(previousCode);
            persistCode(previousCode);
            showApplied(previousCode);
            restoreAppliedDiscountPill(rc);
          } else {
            _liveNativeCode = null;
            clearPersistedCode();
            updateCartUI(await fetchCart());
            hideApplied();
          }
        } else {
          /* Drop the rejected code so it cannot ride along to checkout. */
          await clearCartDiscountCodesAjax();
          _liveNativeCode = null;
          clearPersistedCode();
          updateCartUI(await fetchCart());
          hideApplied();
        }

        /* Restore Tier 2 if the new Shopify code was rejected. */
        if (previousTier2) {
          setAppliedTier2ThemeCode(previousTier2);
          showTier2ThemeApplied(previousTier2);
          await notifyTier2GiftSync();
          previousTier2 = null;
          if (isShippingPage()) {
            await sleep(200);
            await Promise.all([refreshShippingPageSection(), refreshCartDrawer()]);
          } else {
            await refreshCartDrawer();
          }
        }
      }
    } catch (err) {
      showError('Please check the promo code and try again');
      if (previousTier2) {
        try {
          setAppliedTier2ThemeCode(previousTier2);
          showTier2ThemeApplied(previousTier2);
          await notifyTier2GiftSync();
        } catch (_) {}
      }
    } finally {
      hideCartDrawerDiscountLoader();
      if (applyBtn) { applyBtn.disabled = false; applyBtn.textContent = 'Apply'; }
    }
  }

  /* ==========================================================================
     REMOVE DISCOUNT  (user action)
  ========================================================================== */
  async function removeDiscount(clickedBtn) {
    const removeBtn = clickedBtn || document.querySelector('[data-discount-remove]');
    const wrapper   = removeBtn ? removeBtn.closest('[data-cart-discount-wrapper]') : null;
    const input    = wrapper ? wrapper.querySelector('[data-discount-input]') : document.querySelector('[data-discount-input]');
    const pill = removeBtn ? removeBtn.closest('[data-discount-applied]') : null;
    const pillKind = pill ? pill.getAttribute('data-discount-kind') : null;
    const displayCode = pill
      ? pill.querySelector('[data-discount-code-display]')?.textContent?.trim().toUpperCase()
      : null;

    const tier2Code = getAppliedTier2ThemeCode();
    const removingTier2Theme =
      pillKind === 'tier2' ||
      (tier2Code &&
        displayCode &&
        (displayCode === tier2Code.toUpperCase() || isTier2ThemeGiftCode(displayCode)));

    hideError();
    if (removeBtn) removeBtn.disabled = true;
    showSubtotalLoader();
    showCartDrawerDiscountLoader('Removing promo');

    if (removingTier2Theme) {
      clearAppliedTier2ThemeCode();
      hideAppliedKind('tier2');
      if (input) input.value = '';
      try {
        await notifyTier2GiftSync();
        await sleep(200);
        if (isShippingPage()) {
          await Promise.all([refreshShippingPageSection(), refreshCartDrawer()]);
        } else {
          await refreshCartDrawer();
        }
        const cart = await fetchCart();
        restoreAppliedDiscountPill(cart);
      } catch (e) {
        /* ignore — finally hides loader */
      } finally {
        hideCartDrawerDiscountLoader();
        if (removeBtn) removeBtn.disabled = false;
      }
      return;
    }

    /* Wipe everything immediately — before any async work */
    _liveNativeCode = null;
    clearPersistedCode();
    hideAppliedKind('shopify');
    wipeLineBadges();
    if (input) input.value = '';

    try {
      /* Double clear for Shopify session reliability */
      await clearDiscountFromSession();
      await sleep(80);
      await clearDiscountFromSession();

      if (isShippingPage()) {
        /* Brief wait for cart to reflect removal, then refresh both in parallel */
        let cart = await fetchCart();
        for (let i = 0; i < 4 && (cart?.total_discount > 0 || (cart?.discount_codes || []).some(dc => dc.applicable)); i++) {
          await sleep(150);
          cart = await fetchCart();
        }
        /* Removing a code raises the merchandise total — a gift may now qualify. */
        if (await syncFreeGiftAfterDiscountChange()) {
          try { cart = await fetchCart(); } catch (_) {}
        }
        await sleep(150);
        await Promise.all([refreshShippingPageSection(), refreshCartDrawer()]);
        updateCartUI(cart);
        hideSubtotalLoader();
        return;
      }

      /* Brief wait for cart to reflect removal */
      let cart = await fetchCart();
      for (let i = 0; i < 8; i++) {
        const stillApplied = (cart.discount_codes || []).some(
          (dc) =>
            dc.applicable === true &&
            String(dc.code || '').toUpperCase() === removedShopifyCode
        );
        if (!stillApplied) break;
        await sleep(150);
        cart = await fetchCart();
      }

      /* Removing a code raises the merchandise total — a gift may now qualify. */
      if (await syncFreeGiftAfterDiscountChange()) {
        try { cart = await fetchCart(); } catch (_) {}
      }

      /* Update UI immediately for instant feedback, then refresh drawer */
      updateCartUI(cart);
      await refreshCartDrawer();
      updateCartUI(cart);

      if (isShippingPage()) {
        await sleep(150);
        await notifyFreeGiftSync();
        await Promise.all([refreshShippingPageSection(), refreshCartDrawer()]);
        cart = await fetchCart();
        clearPersistedCode();
        updateCartUI(cart);
        restoreAppliedDiscountPill(cart);
        return;
      }

      updateCartUI(cart);
      await notifyFreeGiftSync();
      await refreshCartDrawer();
      cart = await fetchCart();
      clearPersistedCode();
      updateCartUI(cart);
      restoreAppliedDiscountPill(cart);
    } catch (e) {
      window.location.href = '/cart';
    } finally {
      hideCartDrawerDiscountLoader();
      if (removeBtn) removeBtn.disabled = false;
    }
  }

  /* ==========================================================================
     INIT DISCOUNT STATE  (on load / DOMContentLoaded)
  ========================================================================== */
  async function initDiscountState() {
    const savedCode = getPersistedCode();
    const cart      = await fetchCart();

    if (!cart.item_count) {
      if (savedCode) {
        clearPersistedCode();
        clearDiscountFromSession().catch(() => {});
      }
      _liveNativeCode = null;
      const core = getFreeGiftCore();
      if (core && typeof core.clearAppliedTier2Code === 'function') {
        core.clearAppliedTier2Code();
      } else {
        try {
          sessionStorage.removeItem('ck_tier2_gift_code');
          localStorage.removeItem('ck_tier2_gift_code');
        } catch (e) {}
      }
      hideApplied();
      return;
    }

    captureTier2CodeFromDiscountLink();

    // Tier 2 theme code: free-gift.js is the single source of truth for clearing
    // when the cart is truly under threshold. Do not clear here on section refresh —
    // post-assign CT lag + discounted line prices falsely failed meetsTier2Threshold
    // and wiped BISCOTTI (then Tier 1 auto-added). Empty-cart clear above still runs.

    let cartForUi = await applyNativeDiscountFromLinkCookieIfNeeded(cart);

    const liveCode = getApplicableDiscountCode(cartForUi);

    if (liveCode) {
      clearCheckoutFlag();
      const cleanCart = Object.assign({}, cartForUi, {
        discount_codes: (cartForUi.discount_codes || []).filter(
          dc => dc.code.toLowerCase() === liveCode.toLowerCase()
        )
      });
      persistCode(liveCode);
      updateCartUI(cleanCart);
      restoreAppliedDiscountPill(cleanCart);
      return;
    }

    if (savedCode && !isTier2ThemeGiftCode(savedCode)) {
      const checkoutCode = getCheckoutFlag();

      if (checkoutCode && checkoutCode.toLowerCase() === savedCode.toLowerCase()) {
        clearPersistedCode();
        clearCheckoutFlag();
        updateCartUI(cart);
        restoreAppliedDiscountPill(cart);
        return;
      }

      const { success, cart: uc } = await applyCodeToSession(savedCode, 3);
      let settledCart = success ? uc : cart;
      if (success && (await syncFreeGiftAfterDiscountChange())) {
        try { settledCart = await fetchCart(); } catch (_) {}
      }
      if (!success) clearPersistedCode();
      updateCartUI(settledCart);
      restoreAppliedDiscountPill(settledCart);
      return;
    }

    updateCartUI(cartForUi);
    restoreAppliedDiscountPill(cartForUi);
  }

  /* ==========================================================================
     WIRE UP  (event delegation so cart drawer + shipping page both work)
  ========================================================================== */
  function initDiscount() {
    // Clear stale full-drawer overlay (regression: pill-sync used discount-code-loading on drawer).
    _drawerDiscountLoaderActive = false;
    _drawerGiftSyncLoaderActive = false;
    hideCartDrawerDiscountLoader();
    hideCartDrawerGiftSyncLoader();

    insertDiscountUI();

    document.body.addEventListener('click', (e) => {
      const applyBtn = e.target.closest('[data-discount-apply]');
      if (applyBtn) { e.preventDefault(); applyDiscount(applyBtn); return; }
      const removeBtn = e.target.closest('[data-discount-remove]');
      if (removeBtn) { e.preventDefault(); removeDiscount(removeBtn); return; }
    });

    document.body.addEventListener('keypress', (e) => {
      if (e.key !== 'Enter') return;
      const input = e.target.closest('[data-discount-input]');
      if (input) {
        e.preventDefault();
        const applyBtn = input.closest('[data-cart-discount-wrapper]')?.querySelector('[data-discount-apply]');
        applyDiscount(applyBtn || null);
      }
    });

    document.body.addEventListener('input', (e) => {
      if (e.target.matches('[data-discount-input]')) hideError();
    });

    document.body.addEventListener('focus', (e) => {
      if (e.target.matches('[data-discount-input]') && window.innerWidth <= 749) {
        e.target.classList.remove('has-error-mobile');
      }
    }, true);

    initDiscountState();

    ensureDiscountUiReady();

    document.addEventListener('shipping-page:section-refreshed', () => {
      if (!isShippingPage()) return;
      restoreShippingDiscountPillsImmediate(null);
      scheduleShippingPillFollowUp();
    });

    document.addEventListener('free-gift:sync-start', () => {
      if (isShippingPage()) {
        showDiscountPillSyncLoader('Updating your free gift…');
      } else if (isCartDrawerOpen()) {
        showCartDrawerGiftSyncLoader('Updating your free gift…');
      }
    });

    document.addEventListener('free-gift:tier2-code-changed', () => {
      if (isShippingPage()) {
        restoreShippingDiscountPillsImmediate(null);
        scheduleShippingPillFollowUp();
      } else {
        restoreAppliedDiscountPill(null);
      }
    });

    document.addEventListener('free-gift:tier2-code-cleared', () => {
      hideError();
      if (isShippingPage()) {
        restoreShippingDiscountPillsImmediate(null);
        scheduleShippingPillFollowUp();
      } else {
        restoreAppliedDiscountPill(null);
      }
    });

    document.addEventListener('free-gift:sync-idle', (e) => {
      const mutated = !!(e && e.detail && e.detail.mutated);
      if (isShippingPage()) {
        /* No-op gift sync (tab focus / settle poll): restore pills silently — no promo loader. */
        if (!mutated) {
          restoreShippingDiscountPillsImmediate(null);
          return;
        }
        void runShippingPillRestoreFromIdle();
        return;
      }
      if (isCartDrawerOpen()) {
        if (!mutated) {
          void restoreDiscountPillsLight(null);
          return;
        }
        void restoreCartDrawerAfterGiftSync(null);
        return;
      }
      hideCartDrawerGiftSyncLoader();
      void restoreDiscountPillsLight(null);
    });

    document.addEventListener('cart:updated', (e) => {
      const src = e && e.detail ? e.detail.source : null;
      if (src === 'discount-minicart') return;
      if (!isShippingPage() && isCartDrawerOpen() && src !== 'free-gift') {
        showCartDrawerGiftSyncLoader('Updating your free gift…');
      }
      /* free-gift no-op publishes are rare; free-gift mutations already restore via sync-idle. */
      if (isShippingPage() && src === 'free-gift') {
        restoreShippingDiscountPillsImmediate(e && e.detail ? e.detail.cartData : null);
        return;
      }
      scheduleRestoreDiscountPills(e && e.detail ? e.detail.cartData : null);
    });

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (!event || event.source === 'discount-minicart') return;
        if (!isShippingPage() && isCartDrawerOpen() && event.source !== 'free-gift') {
          showCartDrawerGiftSyncLoader('Updating your free gift…');
        }
        scheduleRestoreDiscountPills(event.cartData || null);
      });
    }

    // FreeGift→Bundle / multiship section swap: re-show discount pills from storage.
    document.addEventListener('multiship:restore-tier2-discount-pill', () => {
      if (isShippingPage()) {
        restoreShippingDiscountPillsImmediate(null);
        scheduleRestoreDiscountPills(null);
        scheduleShippingPillFollowUp();
      } else {
        restoreAppliedDiscountPill(null);
      }
    });

    document.addEventListener('theme:cart-drawer:open', () => {
      if (!_drawerDiscountLoaderActive) {
        hideCartDrawerDiscountLoader();
      }
      if (!_drawerGiftSyncLoaderActive) {
        hideCartDrawerGiftSyncLoader();
      }
      reapplyOnReturn();
    });
    document.addEventListener('theme:cart:update', () => {
      if (!isShippingPage()) refreshCart(false);
    });
    document.addEventListener('theme:cart:change', () => {
      if (!isShippingPage()) refreshCart(false);
    });

    interceptCheckout();
    bindVisibilityReapply();
  }

  /* ==========================================================================
     BOOT
  ========================================================================== */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDiscount);
  } else {
    initDiscount();
  }

})();